import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TicketService } from 'src/app/travelx/tickets/ticket.service';
import {
    deleteAttachment,
    deleteAttachmentFailure,
    deleteAttachmentSuccess,
    loadTicketAttachments,
    loadTicketAttachmentsFailure,
    loadTicketAttachmentsSuccess,
    updateAttachment,
    uploadAttachment,
    uploadAttachmentFailure,
    uploadAttachmentSuccess
} from 'src/app/root-store/ticket-store/actions/tickets.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';

@Injectable()
export class TicketAttachmentsEffect {
    constructor(private readonly _actions$: Actions, private readonly _ticketService: TicketService) {}

    getTicketAttachments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadTicketAttachments, uploadAttachmentSuccess, deleteAttachmentSuccess),
            switchMap((action) => {
                return this._ticketService.getTicketAttachedDocs(action.ticketId).pipe(
                    map((result) => loadTicketAttachmentsSuccess({ ticketAttachments: result })),
                    catchError((error) =>
                        of(loadTicketAttachmentsFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    uploadAttachments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(uploadAttachment),
            switchMap((action) => {
                const sub: Observable<unknown>[] = [];
                action.files.forEach((file) => {
                    const file$: Observable<unknown> = this._ticketService.uploadFile(action.ticketId, file);
                    sub.push(file$);
                });
                return forkJoin(sub).pipe(
                    map(() =>
                        uploadAttachmentSuccess({
                            ticketId: action.ticketId,
                            message: 'Successfully uploaded Attachment'
                        })
                    ),
                    catchError((error) =>
                        of(uploadAttachmentFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    updateAttachments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAttachment),
            switchMap((action) => {
                const sub: Observable<unknown>[] = [];
                action.files.forEach((file) => {
                    const file$: Observable<unknown> = this._ticketService.uploadFile(
                        action.ticketId,
                        file,
                        action.attachmentId
                    );
                    sub.push(file$);
                });
                return forkJoin(sub).pipe(
                    map(() =>
                        uploadAttachmentSuccess({
                            ticketId: action.ticketId,
                            message: 'Successfully updated Attachment'
                        })
                    ),
                    catchError((error) =>
                        of(uploadAttachmentFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    deleteAttachments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteAttachment),
            switchMap((action) => {
                return this._ticketService.deleteAttachment(action.ticketId, action.attachmentId).pipe(
                    map(() =>
                        deleteAttachmentSuccess({
                            ticketId: action.ticketId,
                            message: 'Successfully deleted Attachment'
                        })
                    ),
                    catchError((error) =>
                        of(deleteAttachmentFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    showMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(uploadAttachmentSuccess, deleteAttachmentSuccess),
            map((action) => showToasterMessage({ message: action.message }))
        )
    );

    showMessageFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(uploadAttachmentFailure),
            map((action) => showToasterMessage({ message: action.error as string }))
        )
    );
}
